import { useEffect, useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMapMarkerAlt,
  faEnvelope,
  faPhone,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import "./ProfileCard.css";
import { FIRM_IMAGE_BASE } from "../../../config/env";
import ProfileImageLg from "../../CommonComponent/ProfileImageLg";
import { useAuthContext } from "../../../context/AuthContext";
import Swal from "sweetalert2";
import swalWithBootstrapButtons from "sweetalert2-react-content";
import updateIsAdvertised from "../../../apis/updateIsAdvertised";
import updateSelfService from "../../../apis/updateSelfService";
import sendClientQuery from "../../../apis/sendClientQuery";
import SkillSetV2 from "./SkillSetV2";
import CertificationSetV2 from "./CertificationSetV2";
import LanguageV2 from "./LanguageV2";
import AvailabilityV2 from "./AvailabilityV2";
import DocumentBoxV2 from "./DocumentBoxV2";
import { Button } from "react-bootstrap";
import RecommendModal from "./RecommendModal";
import CustomTabs from "./CustomTabs";
import { Link } from "react-router-dom";
import changeFavorite from "../../../apis/changeFavorite";
import { toast } from "react-toastify";
import updateProfileFromResume from "../../../apis/updateProfileFromResume";

const ProfileCard = (props) => {
  const MySwal = swalWithBootstrapButtons(Swal);

  const { displayView, resourceDetails, resourceSlug } = props;

  const { userDetails } = useAuthContext();
  const user_slug = JSON.parse(userDetails).user_slug;

  const user_id = JSON.parse(userDetails).user_id;
  const firm_id = JSON.parse(userDetails).firm_details?.firm_id;
  const firmType = JSON.parse(userDetails).firm_details?.firm_type;
  const firmId = JSON.parse(userDetails).firm_details?.firm_id;
  const user_primary_role = JSON.parse(userDetails).user_primary_role;

  const [isAdvertised, setIsAdvertised] = useState(true);
  const [firmSelfServiceIds, setFirmSelfServiceIds] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);

  const [inquiry, setInquiry] = useState("");
  const [isQuerySendButton, setIsQuerySendButton] = useState(false);
  const [querySendButtonText, setQuerySendButtonText] = useState("Submit");
  const [isFav, setIsFav] = useState(resourceDetails?.profile_details?.isFav);

  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Allow PDF, Word, and OpenOffice formats
    const allowedExtensions = ["pdf", "doc", "docx", "odt", "ott", "rtf"];
    const fileExt = file.name.split(".").pop().toLowerCase();

    if (!allowedExtensions.includes(fileExt)) {
      toast.error("Only PDF, DOC, DOCX, ODT, OTT, or RTF files are allowed!");
      e.target.value = "";
      return;
    }

    handleUpload(file);
  };

  const handleUpload = (fileToUpload) => {
    setIsUploading(true);
    setProgress(0);

    const data = {
      user_slug,
      file: fileToUpload,
    };

    updateProfileFromResume(data, (percent) => {
      setProgress(percent);
    })
      .then((res) => {
        setIsUploading(false);
        setProgress(100);

        if (res?.data) {
          if (res.data.status === 0) {
            toast.error(res.data.message || "Update failed");
          } else {
            toast.success(res.data.message || "Updated successfully!");
          }

          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          toast.error("Invalid server response");
        }

        // reset input so same file can be re-uploaded
        if (fileInputRef.current) fileInputRef.current.value = "";
        setTimeout(() => setProgress(0), 1000); // hide progress bar after 1s
      })
      .catch(() => {
        setIsUploading(false);
        toast.error("Upload failed. Try again.");
        if (fileInputRef.current) fileInputRef.current.value = "";
        setProgress(0);
      });
  };

  useEffect(() => {
    setFirmSelfServiceIds(
      resourceDetails?.profile_details?.firm_ids_self_services,
    );
    setIsAdvertised(resourceDetails?.profile_details?.is_advertised);
  }, [resourceDetails]);

  const updateAdvertised = (advertised, resourceSlug) => {
    Promise.all([updateIsAdvertised(user_slug, advertised, resourceSlug)])
      .then(async ([data]) => {
        if (data?.data?.status === 1) {
          if (advertised === "0") {
            advertised = "1";
          } else {
            advertised = "0";
          }

          setIsAdvertised(advertised);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const updateFavorite = (status) => {
    const resource_id = resourceDetails?.profile_details?.user_id;

    Promise.all([changeFavorite(user_id, resource_id)])
      .then(async ([data]) => {
        if (status === 2) {
          setIsFav(1);
        } else {
          setIsFav(2);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const displayAdvertise = () => {
    return (
      <>
        <div className="d-flex align-items-center">
          {user_id === resourceDetails?.rm_details.resource_manager_id ||
          resourceDetails?.profile_details?.firm_id === firm_id ? (
            <div className="form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                onChange={() =>
                  updateAdvertised(
                    isAdvertised,
                    resourceDetails.profile_details.user_slug,
                  )
                }
                checked={isAdvertised === "1" ? true : false}
              />
            </div>
          ) : null}

          <label
            className="custom-control-label switch-label"
            htmlFor="visibleSwitch"
          >
            Visible to Clients
          </label>
        </div>
      </>
    );
  };

  const updateSelfServiceEdit = (resourceSlug, name) => {
    MySwal.fire({
      title: "Are you sure?",
      text: `You want to update self-service access for ${name}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      confirmButtonColor: "var(--danger)",
      cancelButtonColor: "var(--black)",
    }).then(async (result) => {
      if (result.isConfirmed) {
        processUpdateSelfService(resourceSlug);
      } else {
        return false;
      }
    });
  };

  const processUpdateSelfService = (resourceSlug) => {
    Promise.all([updateSelfService(user_slug, resourceSlug)])
      .then(async ([data]) => {
        if (data?.data?.status === 1) {
          setFirmSelfServiceIds(data?.data?.resource?.firm_ids_self_services);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleCloseModal = () => setShowModal(false);

  const displayRecommendButton = () => {
    return (
      <>
        <Button
          variant={"success"}
          size="sm"
          onClick={() => setShowModal(true)}
        >
          {`Recommend`}
        </Button>
        <RecommendModal
          show={showModal}
          handleClose={handleCloseModal}
          selectedClients={selectedClients}
          setSelectedClients={setSelectedClients}
          resourceSlug={resourceDetails.profile_details.user_slug}
        />
      </>
    );
  };

  const checkSelfServices = (firmIds) => {
    if (firmIds !== null) {
      const fIds = firmIds.split(", ");
      if (fIds.indexOf(firmId) !== -1) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  const displaySelfService = () => {
    return (
      <div className="d-flex align-items-center">
        {user_id === resourceDetails?.rm_details.resource_manager_id ? (
          <div className="form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              onChange={() =>
                updateSelfServiceEdit(
                  resourceDetails?.profile_details?.user_slug,
                  resourceDetails?.profile_details?.profile_name,
                )
              }
              checked={checkSelfServices(firmSelfServiceIds)}
            />
          </div>
        ) : null}
        <label
          className="custom-control-label switch-label"
          htmlFor="visibleSwitch"
        >
          Self Service
        </label>
      </div>
    );
  };

  const changehandler = (e) => {
    setInquiry(e.target.value);
  };

  const handleData = () => {
    setIsQuerySendButton(true);
    setQuerySendButtonText("Sending...");

    if (inquiry.trim().length > 0) {
      let formData = {
        user_slug: user_slug,
        resource_slug: resourceDetails?.profile_details?.user_slug,
        message: inquiry,
      };

      try {
        sendClientQuery(formData).then(async (data) => {
          if (data?.data) {
            if (data.data.status === 1) {
              await MySwal.fire({
                title: <strong>Success</strong>,
                html: <i>{data.data.message}</i>,
                icon: "success",
              });
              await setInquiry("");
            } else {
              await MySwal.fire({
                title: <strong>Error</strong>,
                html: <i>{data.data.message}</i>,
                icon: "danger",
              });
              await setInquiry("");
            }
          } else {
            await MySwal.fire({
              title: <strong>Error</strong>,
              html: <i>{"Something went wrong. Please try again later."}</i>,
              icon: "danger",
            });
          }
        });

        setIsQuerySendButton(false);
        setQuerySendButtonText("Submit");
      } catch (error) {
        setIsQuerySendButton(false);
        setQuerySendButtonText("Submit");
      }
    } else {
      setIsQuerySendButton(false);
      setQuerySendButtonText("Submit");
    }
  };

  return (
    <div className="my-4">
      <div className="card">
        <div className="p-3 pb-0 d-flex flex-column flex-md-row align-items-start justify-content-between gap-3s">
          <div className="d-flex align-items-center">
            <div className="me-3 position-relative">
              <ProfileImageLg
                imgSrc={
                  displayView === "default"
                    ? resourceDetails?.profile_details.profile_image_path
                    : "assets/uploads/profile/default/default.png"
                }
              />
              <div
                style={{
                  position: "absolute",
                  zIndex: 10,
                  top: "3.6rem",
                  right: "-0.5rem",
                }}
                onClick={() => updateFavorite(isFav)}
              >
                <FontAwesomeIcon
                  icon={faStar}
                  size="2x"
                  color={
                    isFav === 2
                      ? "rgba(233, 226, 209, 1)"
                      : "rgba(250, 134, 1, 1)"
                  }
                />
              </div>
            </div>
            <div>
              {displayView === "default" ? (
                <div className="text-md fw-bold">
                  {resourceDetails?.profile_details?.profile_name}
                </div>
              ) : null}

              {resourceDetails?.profile_details?.user_profile_role ? (
                displayView === "default" ? (
                  <span className="text-link">
                    {resourceDetails.profile_details.user_profile_role}
                  </span>
                ) : (
                  <span className="text-dark fs-6">
                    {resourceDetails.profile_details.user_profile_role}
                  </span>
                )
              ) : (
                <span className="text-muted fst-italic">Not specified</span>
              )}

              {displayView === "default" ? (
                <>
                  <br />
                  <span className="text-muted text-sm">
                    Added by: {resourceDetails?.rm_details?.rm_name}
                  </span>
                </>
              ) : null}
            </div>
          </div>
          {displayView === "default" ? (
            <div className="d-flex mt-3 mt-md-0 align-items-center">
              {user_primary_role === "2" ? (
                <>
                  <div className="custom-control custom-switch me-3">
                    {displayAdvertise()}
                  </div>
                  <div className="custom-control custom-switch me-3">
                    {firmType === "2" ? displaySelfService() : null}
                  </div>
                </>
              ) : null}

              <div className="btn-group" role="group" aria-label="View toggle">
                {user_primary_role === "2" ? (
                  <div className="me-3">{displayRecommendButton()}</div>
                ) : null}
                <>
                  {user_primary_role === "3" ? (
                    <div className="me-3 d-inline-block position-relative">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.odt,.ott,.rtf"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                      />

                      <button
                        className="btn btn-success btn-sm"
                        onClick={() =>
                          fileInputRef.current && fileInputRef.current.click()
                        }
                        disabled={isUploading}
                      >
                        {isUploading
                          ? `Uploading... ${progress}%`
                          : "Update via Resume"}
                      </button>

                      {isUploading && (
                        <div
                          className="progress mt-2"
                          style={{
                            height: "6px",
                            width: "100%",
                            maxWidth: "160px",
                          }}
                        >
                          <div
                            className="progress-bar progress-bar-striped progress-bar-animated"
                            role="progressbar"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      )}
                    </div>
                  ) : null}

                  <Link
                    to={
                      "/resources/edit-resource/" +
                      resourceDetails?.profile_details?.user_slug
                    }
                  >
                    <button className="btn btn-outline-secondary btn-sm">
                      Edit Profile
                    </button>
                  </Link>
                </>
              </div>
            </div>
          ) : (
            <div className="col-8">
              <div className="mb-2">
                <textarea
                  className="form-control"
                  id="inquire-text"
                  rows="2"
                  placeholder={`Ask a question about this candidate to ${resourceDetails?.rm_details?.rm_name} at ${resourceDetails?.profile_details?.firm_name}.`}
                  onChange={changehandler}
                  value={inquiry}
                ></textarea>
              </div>

              <div>
                <div className="d-flex justify-content-end">
                  <Button
                    variant={"primary"}
                    size="sm"
                    onClick={handleData}
                    disabled={isQuerySendButton}
                  >
                    {querySendButtonText}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Contact + Logo */}
        <div className="d-flex p-3 justify-content-center align-items-center">
          <div className="col-md-8">
            <div className="d-flex justify-content-between align-items-center text-muted">
              <span className="me-3 text-sm">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
                {[
                  resourceDetails?.contact_details?.city_name || "Not found",
                  resourceDetails?.contact_details?.state_name || "Not found",
                  resourceDetails?.contact_details?.country_name || "Not found",
                ].join(", ")}
              </span>
              {displayView === "default" ? (
                <>
                  <span className="me-3 text-sm">
                    <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                    {resourceDetails?.contact_details?.user_email ||
                      "Not found"}
                  </span>
                  <span className="text-sm">
                    <FontAwesomeIcon icon={faPhone} className="me-2" />
                    {resourceDetails?.contact_details?.mobile || "Not found"}
                  </span>
                </>
              ) : null}
            </div>
          </div>
          <div className="col-md-4 d-flex justify-content-md-end align-items-end mt-3 mt-md-0">
            <img
              src={`${FIRM_IMAGE_BASE}${resourceDetails?.profile_details?.firm_logo}`}
              alt="Company Logo"
              className="firm-logo-sm-custom-v2"
            />
          </div>
        </div>

        {/* Skills & Details */}
        <div className="row text-md-left p-3 border-top">
          <div className="col-md">
            <SkillSetV2
              displayView={displayView}
              skillDetails={resourceDetails.skill_details}
              skillCount={resourceDetails.skill_count}
              skillFormattedSet={resourceDetails.skill_formatted_set}
              resourceSlug={resourceSlug}
              profileName={resourceDetails.profile_details.profile_name}
            />
          </div>
          <div className="col-md divider-col">
            <CertificationSetV2
              displayView={displayView}
              certificationDetails={resourceDetails.certification_details}
              resourceSlug={resourceSlug}
            />
          </div>
          <div className="col-md divider-col">
            <LanguageV2
              languageDetails={resourceDetails.language_details_V2}
              resourceSlug={resourceSlug}
              displayView={displayView}
              profileName={resourceDetails.profile_details.profile_name}
            />
          </div>
          <div className="col-md divider-col">
            <AvailabilityV2
              availabilityDetails={resourceDetails.availability_details}
            />
          </div>
          {displayView === "default" ? (
            <div className="col-md divider-col">
              <DocumentBoxV2
                documentDetails={resourceDetails.document_details}
              />
            </div>
          ) : null}
        </div>
      </div>
      <div className="my-4">
        <CustomTabs
          resourceDetails={resourceDetails}
          displayView={displayView}
        />
      </div>
    </div>
  );
};

export default ProfileCard;
